import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Search, ChevronLeft, ChevronRight, Activity as ActivityIcon, CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { cn } from "../lib/utils";

const KIND_META = {
  info: { icon: Info, color: "text-blue-600 bg-blue-100 dark:bg-blue-950/40" },
  success: { icon: CheckCircle2, color: "text-green-600 bg-green-100 dark:bg-green-950/40" },
  warning: { icon: AlertTriangle, color: "text-amber-600 bg-amber-100 dark:bg-amber-950/40" },
  critical: { icon: AlertTriangle, color: "text-red-600 bg-red-100 dark:bg-red-950/40" },
};

const PAGE_SIZE = 25;

export default function ActivityLogPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (kind) params.set("kind", kind);
      params.set("limit", PAGE_SIZE);
      params.set("skip", (page - 1) * PAGE_SIZE);
      const { data } = await api.get(`/activity-log?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, kind]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Activity Log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Immutable audit trail of every meaningful action across CCAMS.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
                     className="pl-9" placeholder="Search actor, action, target…" />
            </div>
            <select value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }}
                    data-testid="activity-kind-filter"
                    className="h-10 w-full sm:w-44 rounded-md border border-border bg-card px-3 text-sm">
              <option value="">All kinds</option>
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
            <Button variant="outline" onClick={() => { setPage(1); load(); }}>Search</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading && <div className="py-8 text-center text-muted-foreground">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="py-12 text-center text-muted-foreground flex flex-col items-center gap-2">
              <ActivityIcon className="w-8 h-8" />
              No activity matches your filters.
            </div>
          )}
          <ul className="divide-y divide-border">
            {items.map((a) => {
              const meta = KIND_META[a.kind] || KIND_META.info;
              const Icon = meta.icon;
              return (
                <li key={a.id} data-testid={`activity-item-${a.id}`} className="px-6 py-3 flex gap-3">
                  <div className={cn("w-8 h-8 rounded-md flex items-center justify-center shrink-0", meta.color)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-medium">{a.actor_name || "System"}</span>{" "}
                      <span className="text-muted-foreground">{a.action}</span>{" "}
                      <span className="font-medium">{a.target}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {a.created_at && `${format(new Date(a.created_at), "MMM d, yyyy HH:mm")} · ${formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}`}
                    </div>
                  </div>
                  <Badge variant={a.kind === "critical" ? "critical" : a.kind === "warning" ? "warning" : a.kind === "success" ? "success" : "info"}>
                    {a.kind}
                  </Badge>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between px-6 py-3 border-t border-border text-sm text-muted-foreground">
            <span>
              Showing {items.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–
              {(page - 1) * PAGE_SIZE + items.length} of {total}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="activity-prev">
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <span className="px-2 text-xs">Page {page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="activity-next">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
